// Vendor
import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';

// Redux
import { RootState } from 'src/store';
import {
  cancelReviewEntryRequestThunk,
  escalateThunk,
  reportPlatformFeedbackThunk,
  getPendingReviewEntryThunk,
  getSocketTokenThunk,
  getSpecialtyQueuePendingCountThunk,
  requestReviewEntryThunk
} from './services';

// Cross-slice imports for listening to review completion events
import { sendReportThunk as ivSendReportThunk } from '../incomeVerification/services';
import { sendReportThunk as fdSendReportThunk } from '../entries/services';

// Types
import { IEntry, IRejectedAction, StatusType } from 'src/ts/interfaces';

// Constants
import { NAME } from './constants';

// Enums
import { Status as StatusEnum } from 'src/ts/enums';

type StateData = {
  token: string | null;
  requestEnqueued: boolean;
  assignedEntry: IEntry | null;
  getPendingReviewState: {
    status: StatusType;
    error?: string | null;
  };
  requestReviewState: {
    status: StatusType;
    error?: string | null;
  };
  escalate: { status: StatusType; error?: string | null };
  reportPlatformFeedback: { status: StatusType; error?: string | null };
  cancel: { status: StatusType; error?: string | null };
  getTokenState: { status: StatusType; error?: string | null };
  specialtyQueuePendingCount: number | null;
};

export const initialState: StateData = {
  token: null,
  requestEnqueued: false,
  assignedEntry: null,
  getPendingReviewState: {
    status: StatusEnum.IDLE,
    error: null
  },
  requestReviewState: {
    status: StatusEnum.IDLE,
    error: null
  },
  escalate: { status: StatusEnum.IDLE, error: null },
  reportPlatformFeedback: { status: StatusEnum.IDLE, error: null },
  cancel: { status: StatusEnum.IDLE, error: null },
  getTokenState: { status: StatusEnum.IDLE, error: null },
  specialtyQueuePendingCount: null
};

export const myWorkSlice = createSlice({
  name: NAME,
  initialState,
  reducers: {
    reset: () => {
      return {
        ...initialState
      };
    },
    setAssignedEntry: (state: StateData, action) => {
      const response = action.payload;
      state.getPendingReviewState.status = StatusEnum.SUCCESS;
      state.assignedEntry = response as IEntry;
      state.requestEnqueued = false;
    },
    // After a released review the server has taken the entry back but kept the
    // analyst in line, so My Work should show "waiting" and poll for the next one.
    clearAssignmentAfterPlatformFeedback: (state: StateData) => {
      state.assignedEntry = null;
      state.requestEnqueued = true;
      state.reportPlatformFeedback = { status: StatusEnum.IDLE, error: null };
    }
  },
  extraReducers: (builder) => {
    getPendingReviewEntryReducer(builder);
    requestReviewEntryReducer(builder);
    escalateReducer(builder);
    reportPlatformFeedbackReducer(builder);
    cancelReducer(builder);
    getTokenStateReducer(builder);
    specialtyQueuePendingCountReducer(builder);

    // Reset request state when Income Verification review is completed
    builder.addCase(ivSendReportThunk.fulfilled, (state: StateData) => {
      state.requestEnqueued = false;
      state.assignedEntry = null;
    });

    // Reset request state when fraud detection review is completed
    builder.addCase(fdSendReportThunk.fulfilled, (state: StateData) => {
      state.requestEnqueued = false;
      state.assignedEntry = null;
    });
  }
});

const getPendingReviewEntryReducer = (builder: ActionReducerMapBuilder<StateData>) => {
  builder.addCase(getPendingReviewEntryThunk.pending, (state: StateData) => {
    state.getPendingReviewState = {
      status: StatusEnum.LOADING,
      error: null
    };
  });

  builder.addCase(getPendingReviewEntryThunk.fulfilled, (state: StateData, action) => {
    const { entry, inQueue } = action.payload;

    state.getPendingReviewState.status = StatusEnum.SUCCESS;
    state.assignedEntry = entry as IEntry;

    if (entry) {
      // Entry found - user has an assigned review
      state.requestEnqueued = false;
    } else if (inQueue) {
      // No entry but user is in queue - restore enqueued state (page refresh scenario)
      state.requestEnqueued = true;
    }
    // If no entry and not in queue, requestEnqueued remains unchanged (allows polling to continue)
  });

  builder.addCase(getPendingReviewEntryThunk.rejected, (state: StateData, action) => {
    if (action.error.stack?.includes('404')) {
      // If no entry was found address it on state
      state.getPendingReviewState.status = StatusEnum.SUCCESS;
      state.assignedEntry = null;
    } else {
      state.getPendingReviewState = {
        status: StatusEnum.ERROR,
        error: action.error?.message
      };
    }
  });
};

const requestReviewEntryReducer = (builder: ActionReducerMapBuilder<StateData>) => {
  builder.addCase(requestReviewEntryThunk.pending, (state: StateData) => {
    state.requestReviewState = {
      status: StatusEnum.LOADING,
      error: null
    };
  });

  builder.addCase(requestReviewEntryThunk.fulfilled, (state: StateData, action) => {
    const response = action.payload;

    state.requestReviewState.status = StatusEnum.SUCCESS;

    if (!response) {
      state.assignedEntry = null;
      state.requestEnqueued = true;
    } else {
      state.assignedEntry = response as IEntry;
      state.requestEnqueued = false;
    }
  });

  builder.addCase(requestReviewEntryThunk.rejected, (state: StateData, action) => {
    if (action.error.stack?.includes('404')) {
      // If no entry was found address it on state
      state.requestReviewState.status = StatusEnum.SUCCESS;
      state.assignedEntry = null;
      state.requestEnqueued = true;
    } else {
      state.requestReviewState = {
        status: StatusEnum.ERROR,
        error: action.error?.message
      };
    }
  });
};

const escalateReducer = (builder: ActionReducerMapBuilder<StateData>) => {
  builder.addCase(escalateThunk.pending, (state: StateData) => {
    state.escalate.status = StatusEnum.LOADING;
    state.escalate.error = null;
  });
  builder.addCase(escalateThunk.fulfilled, (state: StateData, action) => {
    state.escalate.status = StatusEnum.SUCCESS;
  });
  builder.addCase(escalateThunk.rejected, (state: StateData, action: IRejectedAction) => {
    state.escalate.status = StatusEnum.ERROR;
    state.escalate.error = action.error?.message;
  });
};

const reportPlatformFeedbackReducer = (builder: ActionReducerMapBuilder<StateData>) => {
  builder.addCase(reportPlatformFeedbackThunk.pending, (state: StateData) => {
    state.reportPlatformFeedback.status = StatusEnum.LOADING;
    state.reportPlatformFeedback.error = null;
  });
  builder.addCase(reportPlatformFeedbackThunk.fulfilled, (state: StateData) => {
    state.reportPlatformFeedback.status = StatusEnum.SUCCESS;
  });
  builder.addCase(
    reportPlatformFeedbackThunk.rejected,
    (state: StateData, action: IRejectedAction) => {
      state.reportPlatformFeedback.status = StatusEnum.ERROR;
      state.reportPlatformFeedback.error = action.error?.message;
    }
  );
};

const cancelReducer = (builder: ActionReducerMapBuilder<StateData>) => {
  builder.addCase(cancelReviewEntryRequestThunk.pending, (state: StateData) => {
    state.cancel.status = StatusEnum.LOADING;
    state.cancel.error = null;
  });
  builder.addCase(cancelReviewEntryRequestThunk.fulfilled, (state: StateData, action) => {
    state.cancel.status = StatusEnum.SUCCESS;
    state.requestEnqueued = false;
  });
  builder.addCase(
    cancelReviewEntryRequestThunk.rejected,
    (state: StateData, action: IRejectedAction) => {
      state.cancel.status = StatusEnum.ERROR;
      state.cancel.error = action.error?.message;
    }
  );
};

const specialtyQueuePendingCountReducer = (builder: ActionReducerMapBuilder<StateData>) => {
  builder.addCase(getSpecialtyQueuePendingCountThunk.fulfilled, (state: StateData, action) => {
    state.specialtyQueuePendingCount = action.payload;
  });
};

const getTokenStateReducer = (builder: ActionReducerMapBuilder<StateData>) => {
  builder.addCase(getSocketTokenThunk.pending, (state: StateData) => {
    state.getTokenState.status = StatusEnum.LOADING;
    state.getTokenState.error = null;
  });
  builder.addCase(getSocketTokenThunk.fulfilled, (state: StateData, action) => {
    state.getTokenState.status = StatusEnum.SUCCESS;
    state.token = action.payload;
  });
  builder.addCase(getSocketTokenThunk.rejected, (state: StateData, action: IRejectedAction) => {
    state.getTokenState.status = StatusEnum.ERROR;
    state.getTokenState.error = action.error?.message;
  });
};

export const selectors = {
  isLoadingPendingSubmission: (state: RootState) =>
    state[NAME].getPendingReviewState.status === StatusEnum.LOADING,
  isIdlePendingSubmission: (state: RootState) =>
    state[NAME].getPendingReviewState.status === StatusEnum.IDLE,
  isLoadingSubmissionRequest: (state: RootState) =>
    state[NAME].requestReviewState.status === StatusEnum.LOADING,
  isLoadingCancelRequest: (state: RootState) => state[NAME].cancel.status === StatusEnum.LOADING,
  token: (state: RootState) => state[NAME].token,
  requestEnqueued: (state: RootState) => state[NAME].requestEnqueued,
  assignedEntry: (state: RootState) => state[NAME].assignedEntry,
  specialtyQueuePendingCount: (state: RootState) => state[NAME].specialtyQueuePendingCount,
  escalate: {
    status: (state: RootState) => state[NAME].escalate.status,
    error: (state: RootState) => state[NAME].escalate.error
  },
  reportPlatformFeedback: {
    status: (state: RootState) => state[NAME].reportPlatformFeedback.status,
    error: (state: RootState) => state[NAME].reportPlatformFeedback.error
  },
  cancel: {
    status: (state: RootState) => state[NAME].cancel.status,
    error: (state: RootState) => state[NAME].cancel.error
  },
  getTokenState: {
    status: (state: RootState) => state[NAME].getTokenState.status,
    error: (state: RootState) => state[NAME].getTokenState.error
  }
};

export const { reducer, actions } = myWorkSlice;
